// libraries
import React, { useCallback, useContext, useEffect, useRef } from "react";
import { isEmpty } from "@swiggy-private/js-utils";
import {
    TableContext,
    ToastActionType,
    useApiContextFetch,
    useDelayedActionToast,
} from "@swiggy-private/portal-dineout-context";
import { useRouter } from "next/compat/router";

// api
import { triggerQuickAction } from "../api/quickActions";

// constants & types
import { QUERY_PARAMS, ROUTES, STORAGE_KEYS, SUCCESS_STATUS_CODE } from "../constants";
import { DELAYED_TOAST_CONSTANTS } from "../constants/delayedToast";
import { ILiveNotificationBridgeData, JSBridgeTypes } from "../types";

// hooks
import { useIsV2InApp } from "./useIsV2InApp";

// utils
import { registerJSBridgeHandler } from "../utils/globalJsBridgeDispatcher";
import {
    getToastBottomHeightMenuPage,
    getToastBottomHeightOrderPage,
    getToastStyle,
    getToastWidth,
} from "../utils/helper";
import { closeWebview } from "../utils/nativeBridgeHelper";

/**
 * Live Notification CTAs land in the portal through two channels:
 *  - Bridge channel (portal webview open): native calls
 *    `window.globalJSBridgeHandler({ type, data })` → handlers on the shared dispatcher.
 *  - URL channel (no portal webview): native deeplinks `/qr/menu` with `type`,
 *    `actionId`, `actionName`, `userJourney` → picked up on mount via the same handlers.
 *
 * Mount once inside QrLayout (below TableContext.Provider, inside ApiProvider /
 * DelayedActionToastProvider).
 */
const persistUserJourney = (data: ILiveNotificationBridgeData): void => {
    if (isEmpty(data.userJourney)) return;
    try {
        sessionStorage.setItem(STORAGE_KEYS.USER_JOURNEY, data.userJourney);
    } catch {
        // SSR or private browsing — best-effort only
    }
};

type RouterQueryValue = string | string[];
const asString = (value: RouterQueryValue): string => (typeof value === "string" ? value : "");

// Toast wrapper must place the DLS component (it has no positioning of its own).
// Uses menu-page style on /qr/menu, order-page style elsewhere.
const buildLiveNotificationToastStyle = (pathname: string): React.CSSProperties => {
    let sab = "0px";
    try {
        sab = getComputedStyle(document.documentElement).getPropertyValue("--sab")?.trim() || "0px";
    } catch {}

    if (pathname.startsWith(ROUTES.MENU)) {
        return getToastStyle(
            getToastBottomHeightMenuPage(false),
            getToastWidth(
                3 * DELAYED_TOAST_CONSTANTS.DEFAULT_HORIZONTAL_MARGIN + DELAYED_TOAST_CONSTANTS.TOAST_DEFAULT_SIZE,
            ),
            sab,
        ) as React.CSSProperties;
    }

    return getToastStyle(
        getToastBottomHeightOrderPage(DELAYED_TOAST_CONSTANTS.BOTTOM_STICKY_ELEMENT_ID),
        getToastWidth(2 * DELAYED_TOAST_CONSTANTS.DEFAULT_HORIZONTAL_MARGIN),
        sab,
    ) as React.CSSProperties;
};

export const useLiveNotificationHandler = (): void => {
    const router = useRouter();
    const makeApiCall = useApiContextFetch();
    const { tableId = "", restaurantId = "", parentOrderId = "" } = useContext(TableContext);
    const { dispatch: dispatchToast } = useDelayedActionToast();
    const { isV2InApp } = useIsV2InApp();

    const handleOpenMenu = useCallback(
        (data: ILiveNotificationBridgeData): boolean => {
            persistUserJourney(data);
            if (isV2InApp) {
                closeWebview();
            } else {
                router?.replace(ROUTES.MENU);
            }
            return true;
        },
        [isV2InApp, router],
    );

    const handleBillSummary = useCallback(
        (data: ILiveNotificationBridgeData): boolean => {
            persistUserJourney(data);
            router?.replace(ROUTES.BILL_SUMMARY);
            return true;
        },
        [router],
    );

    const handleQuickAction = useCallback(
        (data: ILiveNotificationBridgeData): boolean => {
            persistUserJourney(data);

            const actionId = data.actionId ?? "";
            const actionName = data.actionName || "callWaiter";

            if (!makeApiCall || isEmpty(actionId)) return true;

            const toastStyle = buildLiveNotificationToastStyle(router?.pathname || "");

            // setTimeout(0): useQuickActions dispatches HIDE_TOAST on mount in the same
            // React commit — delaying ensures our SHOW_TOAST fires after that wipe.
            setTimeout(() => {
                dispatchToast({
                    type: ToastActionType.SHOW_TOAST,
                    payload: {
                        style: toastStyle,
                        props: {
                            delayActionDuration: DELAYED_TOAST_CONSTANTS.DELAY_ACTION_DURATION,
                            onAction: async () => {
                                const response = await triggerQuickAction(
                                    {
                                        tableId,
                                        restaurantId,
                                        actionId,
                                        actionName,
                                        ...(parentOrderId && { parentOrderId }),
                                    },
                                    makeApiCall,
                                );
                                if (response?.statusCode !== SUCCESS_STATUS_CODE) {
                                    throw new Error("Action failed");
                                }
                            },
                        },
                    },
                });
            }, 0);

            return true;
        },
        [makeApiCall, tableId, restaurantId, parentOrderId, dispatchToast, router?.pathname],
    );

    useEffect(() => {
        const unregisters = [
            registerJSBridgeHandler(JSBridgeTypes.DO_DIGITAL_MENU_OPEN_MENU, handleOpenMenu),
            registerJSBridgeHandler(JSBridgeTypes.DO_DIGITAL_MENU_QUICK_ACTION, handleQuickAction),
            registerJSBridgeHandler(JSBridgeTypes.DO_DIGITAL_MENU_BILL_SUMMARY, handleBillSummary),
        ];
        return () => unregisters.forEach((u) => u());
    }, [handleOpenMenu, handleQuickAction, handleBillSummary]);

    // URL-channel: fire at most once per (type, actionId) even if deps change.
    const urlDispatchKeyRef = useRef<string>("");
    useEffect(() => {
        if (!router?.isReady) return;

        const type = asString(router.query.type ?? "");
        const userJourney = asString(router.query[QUERY_PARAMS.USER_JOURNEY] ?? "");

        if (userJourney) persistUserJourney({ userJourney });

        if (type !== JSBridgeTypes.DO_DIGITAL_MENU_QUICK_ACTION) return;

        const actionId = asString(router.query.actionId ?? "");
        const actionName = asString(router.query.actionName ?? "");
        const dispatchKey = `${type}|${actionId}`;

        if (!actionId || urlDispatchKeyRef.current === dispatchKey) return;

        urlDispatchKeyRef.current = dispatchKey;
        handleQuickAction({ actionId, actionName, userJourney });
    }, [router?.isReady, router?.query, handleQuickAction]);
};
